import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./ProjectCandidate.css";
import ProjectTabs from "./ProjectTabs";
import { API_BASE } from "../../../ApiUrl";

const PROFESSIONAL_PROFILE_API_URL = `${API_BASE}/professional-profiles/`;
const PROFESSIONAL_SCOPES_API_URL = `${API_BASE}/professional-scopes/`;
const PROJECTS_API_URL = `${API_BASE}/projects/`;

const DISCLOSURE_API_URL = `${API_BASE}/disclosure-requests/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;
const PROJECT_PLACEMENTS_API_URL = `${API_BASE}/project-placements/`;

const emptyRequest = (project, professional) => ({
  id: null,
  project: project || "",
  professional: professional || "",
  purpose: "",
  requested_fields: [],
  field_input: ""
});

const todayISO = () => new Date().toISOString().substring(0, 10);

const ProjectCandidateView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const candidate =
    location.state?.candidate ||
    (id ? { id, project: null, professional: null } : null);

  // ---- Candidate state ----
  const [profile, setProfile] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);

  // ---- Disclosure requests state ----
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState([
    emptyRequest(candidate?.project, candidate?.professional)
  ]);

  const [tenantId, setTenantId] = useState(null);
  const [userId, setUserId] = useState(null);

  // ---- Deployment modal state ----
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [deploymentForm, setDeploymentForm] = useState({
    assigned_role: "",
    deployment_end: ""
  });
  const [savingDeployment, setSavingDeployment] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ---- Session ----
  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) setTenantId(Number(tenantUser.tenant_id));
    if (tenantUser?.user_id) setUserId(Number(tenantUser.user_id));
  }, []);

  // ---- Fetch candidate profile ----
  useEffect(() => {
    const fetchData = async () => {
      if (!candidate?.professional) return;
      try {
        setLoading(true);
        const profileRes = await axios.get(
          `${PROFESSIONAL_PROFILE_API_URL}${candidate.professional}/`
        );
        setProfile(profileRes.data?.data || profileRes.data || null);
      } catch (error) {
        console.error("Error fetching professional profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.professional]);

  // ---- Fetch project ----
  useEffect(() => {
    const fetchProject = async () => {
      if (!candidate?.project) return;
      try {
        const projectRes = await axios.get(
          `${PROJECTS_API_URL}${candidate.project}/`
        );
        setProject(projectRes.data?.data || projectRes.data || null);
      } catch (error) {
        console.error("Error fetching project:", error);
        setProject(null);
      }
    };
    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.project]);

  // ---- Fetch disclosure requests ----
  useEffect(() => {
    if (tenantId) fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchRequests = async () => {
    try {
      setLoadingRequests(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["disclosure_requests"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.disclosure_requests) {
        rows = result.data.disclosure_requests;
      } else if (result?.disclosure_requests) {
        rows = result.disclosure_requests;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      if (candidate?.project) {
        rows = rows.filter(
          (r) => Number(r.project) === Number(candidate.project)
        );
      }
      if (candidate?.professional) {
        rows = rows.filter(
          (r) => Number(r.professional) === Number(candidate.professional)
        );
      }

      setRequests(rows);
    } catch (error) {
      console.error("Error fetching disclosure requests:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load requests",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading requests."
      });
    } finally {
      setLoadingRequests(false);
    }
  };

  // ---- Form handlers ----
  const handlePurposeChange = (index, value) => {
    setFormData((prev) =>
      prev.map((r, i) => (i === index ? { ...r, purpose: value } : r))
    );
  };

  const handleFieldInputChange = (index, value) => {
    setFormData((prev) =>
      prev.map((r, i) => (i === index ? { ...r, field_input: value } : r))
    );
  };

  const addFieldChip = (index) => {
    setFormData((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const raw = (r.field_input || "").trim();
        if (!raw) return { ...r, field_input: "" };
        if (r.requested_fields.includes(raw)) {
          return { ...r, field_input: "" };
        }
        return {
          ...r,
          requested_fields: [...r.requested_fields, raw],
          field_input: ""
        };
      })
    );
  };

  const removeFieldChip = (index, fieldValue) => {
    setFormData((prev) =>
      prev.map((r, i) =>
        i === index
          ? {
              ...r,
              requested_fields: r.requested_fields.filter(
                (f) => f !== fieldValue
              )
            }
          : r
      )
    );
  };

  const handleFieldKeyDown = (index, e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addFieldChip(index);
    } else if (e.key === "Backspace" && !e.target.value) {
      setFormData((prev) =>
        prev.map((r, i) => {
          if (i !== index) return r;
          if (!r.requested_fields.length) return r;
          return {
            ...r,
            requested_fields: r.requested_fields.slice(0, -1)
          };
        })
      );
    }
  };

  const handleAdd = () => {
    setFormData([
      emptyRequest(candidate?.project, candidate?.professional)
    ]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [
      ...prev,
      emptyRequest(candidate?.project, candidate?.professional)
    ]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (r) => {
    let fieldsArr = [];
    if (Array.isArray(r.requested_fields)) {
      fieldsArr = r.requested_fields.map((f) => String(f));
    } else if (typeof r.requested_fields === "string" && r.requested_fields) {
      try {
        const parsed = JSON.parse(r.requested_fields);
        if (Array.isArray(parsed)) fieldsArr = parsed.map((f) => String(f));
      } catch {
        fieldsArr = r.requested_fields
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }

    setFormData([
      {
        id: r.id,
        project: r.project ? String(r.project) : "",
        professional: r.professional ? String(r.professional) : "",
        purpose: r.purpose || "",
        requested_fields: fieldsArr,
        field_input: ""
      }
    ]);
    setEditingId(r.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (r) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Request?",
      text: "Are you sure you want to delete this disclosure request?",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${DISCLOSURE_API_URL}${r.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Disclosure request deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchRequests();
    } catch (error) {
      console.error("Error deleting disclosure request:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the request."
      });
    }
  };

  // ---- Validation ----
  const validateRow = (r, index) => {
    const label = `Request ${index + 1}`;

    if (!r.project) {
      Swal.fire({
        icon: "warning",
        title: "Project Missing",
        text: `Project is missing for ${label}.`
      });
      return false;
    }
    if (!r.professional) {
      Swal.fire({
        icon: "warning",
        title: "Professional Missing",
        text: `Professional is missing for ${label}.`
      });
      return false;
    }
    if (!r.purpose.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Purpose Required",
        text: `Please provide a Purpose for ${label}.`
      });
      return false;
    }
    if (!Array.isArray(r.requested_fields) || !r.requested_fields.length) {
      Swal.fire({
        icon: "warning",
        title: "No Fields Selected",
        text: `Please add at least one requested field for ${label}.`
      });
      return false;
    }
    return true;
  };

  const buildPayload = (r) => ({
    project: Number(r.project),
    professional: Number(r.professional),
    requested_by: userId,
    purpose: r.purpose.trim(),
    requested_fields: r.requested_fields,
    tenant: tenantId
  });

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId || !userId) {
      Swal.fire({
        icon: "error",
        title: "Session missing",
        text: "Tenant ID or User ID is missing."
      });
      return;
    }

    const committed = formData.map((r) => {
      const pending = (r.field_input || "").trim();
      if (pending && !r.requested_fields.includes(pending)) {
        return {
          ...r,
          requested_fields: [...r.requested_fields, pending],
          field_input: ""
        };
      }
      return { ...r, field_input: "" };
    });

    setFormData(committed);

    for (let i = 0; i < committed.length; i++) {
      if (!validateRow(committed[i], i)) return;
    }

    const activeRows = committed.filter((r) => r.project && r.professional);

    const CONFIG = { headers: { "Content-Type": "application/json" } };

    try {
      setLoadingRequests(true);

      if (isEditing) {
        const payload = buildPayload(committed[0]);
        await axios.put(
          `${DISCLOSURE_API_URL}${editingId}/`,
          payload,
          CONFIG
        );

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const r of activeRows) {
          await axios.post(DISCLOSURE_API_URL, buildPayload(r), CONFIG);
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} disclosure request${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([
        emptyRequest(candidate?.project, candidate?.professional)
      ]);

      await fetchRequests();
    } catch (error) {
      console.error("Error saving disclosure request:", error);
      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error)
      });
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([
      emptyRequest(candidate?.project, candidate?.professional)
    ]);
  };

  // ---- Deployment modal handlers ----
  const openDeploymentModal = () => {
    setDeploymentForm({
      assigned_role: "",
      deployment_end: ""
    });
    setShowDeploymentModal(true);
  };

  const closeDeploymentModal = () => {
    if (savingDeployment) return;
    setShowDeploymentModal(false);
  };

  const handleDeploymentChange = (e) => {
    const { name, value } = e.target;
    setDeploymentForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeploymentSubmit = async (e) => {
    e.preventDefault();

    if (!candidate?.project || !candidate?.professional) {
      Swal.fire({
        icon: "error",
        title: "Missing context",
        text: "Project or Professional information is not available."
      });
      return;
    }

    if (!deploymentForm.assigned_role.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Assigned Role Required",
        text: "Please enter an assigned role."
      });
      return;
    }

    if (!deploymentForm.deployment_end) {
      Swal.fire({
        icon: "warning",
        title: "Deployment End Required",
        text: "Please select a deployment end date."
      });
      return;
    }

    if (deploymentForm.deployment_end < todayISO()) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Date",
        text: "Deployment End cannot be before today."
      });
      return;
    }

    const payload = {
      assigned_role: deploymentForm.assigned_role.trim(),
      deployment_start: todayISO(),
      deployment_end: deploymentForm.deployment_end,
      status: "ACTIVE",
      project: Number(candidate.project),
      professional: Number(candidate.professional),
      professional_assignment: candidate?.professional_assignment
        ? Number(candidate.professional_assignment)
        : null
    };

    console.log("Create Deployment Payload:", payload);

    try {
      setSavingDeployment(true);

      const res = await axios.post(PROJECT_PLACEMENTS_API_URL, payload, {
        headers: { "Content-Type": "application/json" }
      });

      console.log("Create Deployment Response:", res.data);

      await Swal.fire({
        icon: "success",
        title: "Deployment Created",
        text: "Deployment has been created successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      setShowDeploymentModal(false);
      setDeploymentForm({ assigned_role: "", deployment_end: "" });
    } catch (error) {
      console.error("Error creating deployment:", error);
      Swal.fire({
        icon: "error",
        title: "Creation Failed",
        text: extractError(error)
      });
    } finally {
      setSavingDeployment(false);
    }
  };

  // ---- Lookup helpers ----
  const getFullName = () => {
    if (!profile) return "—";
    const first = profile.first_name || "";
    const last = profile.last_name || "";
    const full = `${first} ${last}`.trim();
    return full || profile.full_name || "—";
  };

  const getProjectName = () => {
    if (!project)
      return candidate?.project ? `Project ${candidate.project}` : "—";
    return project.project_name || project.name || `Project ${project.id}`;
  };

  const getFieldsArray = (r) => {
    if (Array.isArray(r.requested_fields)) return r.requested_fields;
    if (typeof r.requested_fields === "string" && r.requested_fields) {
      try {
        const parsed = JSON.parse(r.requested_fields);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return r.requested_fields
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    return [];
  };

  const renderField = (label, value) => (
    <div className="candidate-view-field">
      <span className="candidate-view-label">{label}</span>
      <span className="candidate-view-value">
        {value !== undefined && value !== null && value !== ""
          ? value
          : "—"}
      </span>
    </div>
  );

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-membership-page">
          <ProjectTabs />

          {/* ---------- PAGE HEADER ---------- */}
          <div className="tenant-membership-page-header">
            <div>
              <h2>Candidate Details</h2>
            </div>

            <div className="candidate-view-header-actions">
              <button
                type="button"
                className="candidate-view-add-btn"
                onClick={openDeploymentModal}
              >
                + Add Deployment
              </button>

              <button
                type="button"
                className="candidate-view-back-btn"
                onClick={() => navigate("/admin/project-candidates")}
              >
                ← Back
              </button>
            </div>
          </div>

          {/* ---------- SECTION 1: CANDIDATE DETAILS ---------- */}
          {loading ? (
            <div className="tenant-membership-loading">
              Loading candidate details...
            </div>
          ) : (
            <div className="candidate-view-card">
              <div className="candidate-view-card-header">
                <h3>Candidate Overview</h3>
              </div>

              <div className="candidate-view-grid">
                {renderField("Full Name", getFullName())}
                {renderField("Email", profile?.email)}
                {renderField(
                  "Phone",
                  profile?.phone || profile?.mobile_number
                )}
                {renderField(
                  "Job Title",
                  profile?.job_title || profile?.role
                )}
                {renderField("Company", profile?.company)}
                {renderField("Location", profile?.location)}
                {renderField("Project", getProjectName())}
                {renderField("Project Status", project?.status)}
              </div>
            </div>
          )}

          {/* ---------- SECTION 2: DISCLOSURE REQUESTS ---------- */}
          <div className="candidate-view-card">
            <div className="candidate-view-card-header candidate-view-card-header--split">
              <h3>Disclosure Requests</h3>

              {!showForm && (
                <button
                  type="button"
                  className="tenant-membership-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add Request
                </button>
              )}
            </div>

            {!showForm ? (
              loadingRequests ? (
                <div className="tenant-membership-loading">
                  Loading requests...
                </div>
              ) : requests.length === 0 ? (
                <div className="tenant-membership-empty">
                  <div className="tenant-membership-empty-icon">+</div>
                  <h3>No Disclosure Requests</h3>
                  <p>
                    There are no disclosure requests for this candidate yet.
                  </p>
                  <button
                    type="button"
                    className="tenant-membership-empty-add-btn"
                    onClick={handleAdd}
                  >
                    + Add Request
                  </button>
                </div>
              ) : (
                <div className="tenant-membership-table-wrapper">
                  <table className="tenant-membership-table">
                    <thead>
                      <tr>
                        <th>Purpose</th>
                        <th>Requested Fields</th>
                        <th>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {requests.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <span className="disclosure-purpose-cell">
                              {r.purpose || "-"}
                            </span>
                          </td>

                          <td>
                            <div className="tenant-membership-scope-pills">
                              {getFieldsArray(r).length === 0 ? (
                                <span className="tenant-membership-muted">
                                  -
                                </span>
                              ) : (
                                getFieldsArray(r).map((f, i) => (
                                  <span
                                    key={i}
                                    className="tenant-membership-scope-pill"
                                  >
                                    {f}
                                  </span>
                                ))
                              )}
                            </div>
                          </td>

                          <td>
                            <div className="tenant-membership-actions">
                              <button
                                type="button"
                                className="tenant-membership-edit-btn"
                                onClick={() => handleEdit(r)}
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                className="tenant-membership-delete-btn"
                                onClick={() => handleDelete(r)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <form onSubmit={handleSubmit}>
                {formData.map((r, index) => (
                  <div
                    className="tenant-membership-record"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="tenant-membership-record-header">
                      <h4>Request {index + 1}</h4>

                      {!isEditing && formData.length > 1 && (
                        <button
                          type="button"
                          className="tenant-membership-remove-btn"
                          onClick={() => handleRemoveForm(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="tenant-membership-form-grid">
                      <div className="tenant-membership-form-group tenant-membership-form-group--full">
                        <label>
                          Purpose <span>*</span>
                        </label>
                        <textarea
                          className="disclosure-purpose-input"
                          value={r.purpose}
                          onChange={(e) =>
                            handlePurposeChange(index, e.target.value)
                          }
                          placeholder="Describe why these details are being requested"
                          rows={3}
                        />
                      </div>

                      <div className="tenant-membership-form-group tenant-membership-form-group--full">
                        <label>
                          Requested Fields <span>*</span>
                        </label>

                        <div className="tenant-membership-scope-chips">
                          {r.requested_fields.length === 0 &&
                          !r.field_input ? (
                            <span className="tenant-membership-scope-loading">
                              Type a field name and press Enter
                            </span>
                          ) : null}

                          {r.requested_fields.map((field) => (
                            <span
                              className="tenant-membership-scope-chip tenant-membership-scope-chip--active"
                              key={field}
                            >
                              <span>{field}</span>
                              <button
                                type="button"
                                className="tenant-membership-scope-chip-remove"
                                onClick={() =>
                                  removeFieldChip(index, field)
                                }
                                aria-label={`Remove ${field}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}

                          <input
                            type="text"
                            className="tenant-membership-scope-input"
                            value={r.field_input}
                            onChange={(e) =>
                              handleFieldInputChange(index, e.target.value)
                            }
                            onKeyDown={(e) =>
                              handleFieldKeyDown(index, e)
                            }
                            onBlur={() => addFieldChip(index)}
                            placeholder={
                              r.requested_fields.length
                                ? ""
                                : "e.g. email, phone, address"
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="tenant-membership-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Request
                  </button>
                )}

                <div className="tenant-membership-form-actions">
                  <button
                    type="button"
                    className="tenant-membership-cancel-btn"
                    onClick={handleCancel}
                    disabled={loadingRequests}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="tenant-membership-save-btn"
                    disabled={loadingRequests}
                  >
                    {loadingRequests
                      ? "Saving..."
                      : isEditing
                      ? "Update Request"
                      : "Save Requests"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ---------- ADD DEPLOYMENT MODAL ---------- */}
      {showDeploymentModal && (
        <div
          className="deployment-modal-overlay"
          onClick={closeDeploymentModal}
        >
          <div
            className="deployment-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="deployment-modal-header">
              <h3>Add Deployment</h3>
              <button
                type="button"
                className="deployment-modal-close"
                onClick={closeDeploymentModal}
                disabled={savingDeployment}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleDeploymentSubmit}>
              <div className="deployment-modal-body">
                <div className="deployment-modal-field">
                  <label>
                    Assigned Role <span>*</span>
                  </label>
                  <input
                    type="text"
                    name="assigned_role"
                    value={deploymentForm.assigned_role}
                    onChange={handleDeploymentChange}
                    placeholder="Enter assigned role"
                    maxLength={120}
                    autoFocus
                  />
                </div>

                <div className="deployment-modal-field">
                  <label>
                    Deployment End <span>*</span>
                  </label>
                  <input
                    type="date"
                    name="deployment_end"
                    value={deploymentForm.deployment_end}
                    onChange={handleDeploymentChange}
                    min={todayISO()}
                  />
                </div>
              </div>

              <div className="deployment-modal-actions">
                <button
                  type="button"
                  className="deployment-modal-cancel"
                  onClick={closeDeploymentModal}
                  disabled={savingDeployment}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="deployment-modal-save"
                  disabled={savingDeployment}
                >
                  {savingDeployment ? "Saving..." : "Save Deployment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCandidateView;